"use client";

/* CrossSellRail — the inline "While you're here…" strip.
 * Website Domination SOP §6.1 component 3 + §6.2 per-page map.
 *
 * Mounted ONCE in the root layout above the Footer; derives the page
 * key from the pathname so every page gets contextual cross-sell
 * cards with no per-page wiring. Hidden on funnel/auth/admin routes
 * where selling sideways would hurt conversion. Doctrine: contextual,
 * never spam — 4-6 cards max, each a logical next step. */

import Link from "next/link";
import { useSyncExternalStore } from "react";
import { getCrossSells } from "@/lib/cross-sell-map";

/* 2026-09-06: this rail was the homepage's React #418. The prerendered
 * HTML carried the "default" two cards while the browser's first render
 * computed "home" (five) from usePathname(), so React discarded the whole
 * server tree and rebuilt the page client-side — 1.3 s of blocking time
 * on desktop and a 2.5 s LCP render delay on mobile, on every visit.
 * useSyncExternalStore makes the page key an external value: the server
 * snapshot ("default") is what hydration renders, then React swaps in the
 * real key as a normal update. No mismatch, and only this rail re-renders. */
const subscribe = (cb: () => void) => {
  window.addEventListener("popstate", cb);
  return () => window.removeEventListener("popstate", cb);
};
const getClientPath = () => window.location.pathname;
const getServerPath = () => "";

const HIDDEN_PREFIXES = [
  "/auth",
  "/admin",
  "/checkout",
  "/checkout-mock",
  "/order",
  "/thanks",
  "/start",
  "/cart",
];

function pageKeyFromPath(path: string): string {
  if (path === "/") return "home";
  if (path.startsWith("/browse-businesses")) return "browse-businesses";
  if (path.startsWith("/business/")) return "business";
  if (path.startsWith("/notion")) return "notion";
  if (path.startsWith("/templates")) return "templates";
  if (path.startsWith("/3d-models")) return "3d-models";
  if (path.startsWith("/digital-art")) return "digital-art";
  if (path.startsWith("/membership")) return "membership";
  if (path.startsWith("/starter-kits")) return "starter-kits";
  return "default";
}

export function CrossSellRail() {
  const pathname = useSyncExternalStore(subscribe, getClientPath, getServerPath);
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  /* "" (server / hydration) → "default"; the client path fills in after. */
  const cards = getCrossSells(pathname ? pageKeyFromPath(pathname) : "default");
  if (!cards.length) return null;

  return (
    <section
      aria-label="Related across Apex Flow Digital"
      className="px-5 md:px-10 lg:px-16 py-12 border-t"
      style={{ borderColor: "var(--color-border-default)" }}
    >
      <div className="mx-auto max-w-[1500px]">
        <div
          className="font-mono text-[11px] uppercase tracking-[0.16em] mb-5"
          style={{ color: "var(--color-fg-subtle)" }}
        >
          While you&rsquo;re here…
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c) => (
            <Link
              key={c.href + c.title}
              href={c.href}
              className="group flex flex-col rounded-xl border p-5 transition-transform duration-200 hover:-translate-y-0.5"
              style={{
                borderColor: "var(--color-border-default)",
                background: "var(--color-bg-elev-1)",
              }}
            >
              <div
                className="font-mono text-[10px] uppercase tracking-[0.16em] mb-2"
                style={{ color: "var(--color-accent)" }}
              >
                {c.kicker}
              </div>
              <h3 className="text-[15px] font-medium text-white mb-1.5 group-hover:underline underline-offset-4">
                {c.title} →
              </h3>
              <p className="text-[12.5px] leading-[1.5] text-[color:var(--color-fg-muted)]">
                {c.blurb}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
